import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import {
    APP_INITIALIZER,
    NgModule,
    CUSTOM_ELEMENTS_SCHEMA,
    LOCALE_ID,
    ErrorHandler,
} from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { InitService } from './init.service';

import { NgxPermissionsModule } from 'ngx-permissions';

import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import {
    iccIconAlert,
    iccIconNavigateNext,
    iccIconExpandLess,
    iccIconExpandMore,
    iccIconShoppingCart,
    iccIconMenu,
    IconRegistryService,
    iccIconList,
    UiModule,
    iccIconEdit,
    iccIconDone,
    iccIconInfo,
    iccIconHistory,
    iccIconClose,
    iccIconDisabled,
    iccIconAlternative,
    iccIconAddToCart,
    iccIconWarning,
    iccIconPdfFile,
    iccIconFile,
    iccIconImageFile,
    iccIconNavigateTop,
    iccIconDate,
    iccIconOpen,
    iccIconCopy,
} from '@icc/ui';
import { RedirectComponent } from './redirect/redirect.component';

import {
    TranslateModule,
    TranslateLoader,
    TranslateParser,
    TranslateCompiler,
    MissingTranslationHandler,
    TranslateService as NgxTranslateService,
} from '@ngx-translate/core';
import { B2BTranslateService, MyMissingTranslationHandler } from './translate.service';
import { TranslateHttpLoader } from './translate-http-loader';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { B2BTranslateMessageFormatCompiler } from './translate-message-format-compiler';
import { IccTranslateParser } from './translate.parser';
import { UserBoxComponent } from '../../../user-box/src/app/user-box/user-box.component';
import { AnnouncementsComponent } from '../../../user-box/src/app/announcements/announcements.component';
import { ToastComponent } from '../../../user-box/src/app/toast/toast.component';
import { ToastItemComponent } from '../../../user-box/src/app/toast-item/toast-item.component';
import { B2bSalesDocumentModule } from '@icc/b2b/sales-document';
import { HeaderComponent } from './header/header.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import '@angular/common/locales/global/pl';
import { ConfigService, EventBusService, TranslateService } from '@icc/shared';
import { APP_CONFIG, AppConfig, AppConfigFactory } from '@icc/shared/config';
import { B2BSharedModule } from 'libs/b2b/shared/src/lib/shared.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { APP_VERSION } from '@icc/b2b/shared';
import { FooterComponent } from './footer/footer.component';
import * as Sentry from '@sentry/angular-ivy';
import { AnnouncementPreviewComponent } from '../../../user-box/src/app/announcement-preview/announcement-preview.component';
import { ComplaintModule } from '@icc/complaint';
import { ExternalMenuPanelComponent } from './external-menu-panel/external-menu-panel.component';
import { B2bTransportModule } from '@icc/b2b/transport';
import { B2bComplaintsV1Module } from '@icc/b2b/complaints-v1';
import { ConsentComponent } from './consent/consent.component';
import { PanelHttpInterceptor } from './panel-http-interceptor';

export function loadOptionsFactory(initService: InitService) {
    return () => initService.loadOptionsFromServer();
}

export function HttpLoaderFactory(
    http: HttpClient,
    domSanitizer: DomSanitizer,
    configService: ConfigService
) {
    return new TranslateHttpLoader(http, domSanitizer, configService);
}

export function appConfigFactory(initService: InitService): () => AppConfig {
    return () => initService.getAppConfig();
}

export function appLocaleFactory(initService: InitService): string {
    return initService.options.languageCode;
}

@NgModule({
    declarations: [
        AppComponent,
        RedirectComponent,
        UserBoxComponent,
        AnnouncementsComponent,
        ToastComponent,
        ToastItemComponent,
        HeaderComponent,
        FooterComponent,
        AnnouncementPreviewComponent,
        ExternalMenuPanelComponent,
        ConsentComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        HttpClientModule,
        B2BSharedModule,
        B2bSalesDocumentModule,
        ComplaintModule,
        B2bTransportModule,
        B2bComplaintsV1Module,
        NgxPermissionsModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient, DomSanitizer, ConfigService],
            },
            parser: {
                provide: TranslateParser,
                useClass: IccTranslateParser,
                deps: [],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MyMissingTranslationHandler,
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: B2BTranslateMessageFormatCompiler,
            },
        }),
        UiModule,
        StoreModule.forRoot(
            {},
            {
                metaReducers: !environment.production ? [] : [],
                runtimeChecks: {
                    strictActionImmutability: false,
                    strictStateImmutability: false,
                },
            }
        ),
        EffectsModule.forRoot([]),
        [],
        ServiceWorkerModule.register('service-worker.js', {
            enabled: true,
            // Register the ServiceWorker as soon as the app is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:5000',
        }),
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: loadOptionsFactory,
            deps: [InitService, NgxPermissionsService, NgxRolesService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: PanelHttpInterceptor,
            multi: true,
        },
        InitService,
        EventBusService,
        {
            provide: LOCALE_ID,
            useFactory: appLocaleFactory,
            deps: [InitService]
        },
        B2BTranslateService,
        { provide: TranslateService, useExisting: B2BTranslateService },
        { provide: APP_CONFIG, useFactory: appConfigFactory, deps: [InitService] },
        {
            provide: APP_VERSION,
            useValue: environment.appVersion,
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: MESSAGE_FORMAT_CONFIG,
            useValue: {
                strictPluralKeys: false,
            }
        },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(private iconRegistry: IconRegistryService) {
        this.iconRegistry.registerIcons([
            iccIconAlert,
            iccIconWarning,
            iccIconNavigateNext,
            iccIconExpandLess,
            iccIconExpandMore,
            iccIconShoppingCart,
            iccIconMenu,
            iccIconList,
            iccIconEdit,
            iccIconDone,
            iccIconInfo,
            iccIconHistory,
            iccIconClose,
            iccIconDisabled,
            iccIconAlternative,
            iccIconAddToCart,
            iccIconPdfFile,
            iccIconFile,
            iccIconImageFile,
            iccIconNavigateTop,
            iccIconDate,
            iccIconOpen,
            iccIconCopy,
        ]);
    }
}
